import { Container, Graphics, TilingSprite, Texture } from '../../../vendor/pixi.min.mjs';
import {
    PLATFORMER_GRID,
    PLATFORMER_TILE_DEFS,
    PLATFORMER_ENTITY_DEFS,
    PLATFORMER_LAYERS,
    PLATFORMER_PALETTE,
} from './platformerConfig.js';
import { PlatformerFxPreview } from './PlatformerFxPreview.js';
import { autotileMask, autotileRegion, buildAutotileLookup } from './PlatformerAutotile.js';
import {
    drawTilePlaceholder,
    drawEntityPlaceholder,
    paintAreaPlaceholder,
    paintCameraZonePlaceholder,
    paintLightPlaceholder,
    paintPlatformPlaceholder,
} from './PlatformerBoardArt.js';

/**
 * Dual-use platformer board renderer.
 *
 * Used by:
 *   - StageEditorScene (via the schema's renderer adapter, with
 *     `editorMode: true`) to draw the in-editor preview of the
 *     stage as the designer paints.
 *   - PlatformerScene (with `editorMode: false`, the default) to
 *     draw the live game world that the player runs through.
 *
 * Mirrors the public surface that TrainSimBoard exposes to the
 * editor:
 *
 *   const board = new PlatformerBoard(themeManager, opts?);
 *   board.init(stage, gameplayLayerId?);
 *   board.getHitArea();          // editor only
 *   board.destroy();
 *
 * Plus per-cell readers the runtime physics consults:
 *
 *   board.isSolidAt(c, r);       // collision lookup
 *   board.bumpableAt(c, r);      // brick / ?-block
 *   board.tileAt(c, r);          // raw tile object
 *   board.removeTile(c, r);      // bonk-broken brick
 *   board.replaceTile(c, r, type); // ?-block → block.hard
 *   board.iterateEntities(cb);   // stage spawn pass
 *
 * The board doesn't run the physics or move entities — those live in
 * PlatformerPhysics + PlatformerEntities so they can be tested
 * independently. The board only paints, and it owns the gameplay
 * tile grid because that's also the collision grid.
 */
export class PlatformerBoard {
    container = new Container();

    cols = 0;
    rows = 0;
    cellSize = 0;

    /** @type {boolean} Editor mode draws entity placeholders + grid lines. */
    _editorMode = false;

    /** @type {import('../../core/SkinResolver.js').SkinResolver|null} */
    _theme = null;

    /** Map "c,r" → tile object on a full-solid collision layer. */
    _tileGrid = new Map();
    /** Map "c,r" → tile object on a ONE-WAY collision layer. One-way
     *  cells only stop a body falling onto their top edge; they're kept
     *  apart from `_tileGrid` so they never block horizontal motion or
     *  upward jumps (see PlatformerPhysics). */
    _oneWayGrid = new Map();
    /** Map "c,r" → slope tile object. Slopes never join `_tileGrid`
     *  (they'd block as full squares); the physics queries their
     *  surface line through `slopeFloorY` instead. */
    _slopeGrid = new Map();
    /** Per-tile sprite/graphics nodes so we can patch tiles on the fly. */
    _tileNodes = new Map();

    /** @type {Map<string, Container>} Layer id → Container in `container`. */
    _layerContainers = new Map();
    /** @type {object[]} Layers in stage order (bottom → top). */
    _layers = [];

    /** @type {PlatformerFxPreview|null} Editor-only controller for the live
     *  particle-emitter previews (one ParticleSystem per fx.emitter) plus the
     *  selected emitter's force gizmos + demo ghost. Created in the
     *  constructor; advanced by tickParticles() from the editor update loop. */
    _fx = null;

    /** @type {Array<{ts:TilingSprite, parallaxX:number, parallaxY:number,
     *  scrollX:number, scrollY:number, scrollOffX:number, scrollOffY:number,
     *  scale:number, offsetY:number}>} Stacked parallax background layers
     *  (Phase 4). Offset per-frame by applyParallax (camera) + tickBackgrounds
     *  (auto-scroll). */
    _bgLayerNodes = [];

    /** Shared src → Texture cache for background layers, so the same
     *  imported image decodes once across stages / rebuilds. */
    static _bgTexCache = new Map();

    /** Parallax scroll targets, populated in init().
     *  `_parallaxLayers`: [{ container, factor }] — whole decoration layers.
     *  `_parallaxItems`:  [{ node, baseX, itemFactor, layerFactor }] — per-item overrides. */
    _parallaxLayers = [];
    _parallaxItems = [];

    /** Lazy hit area for the editor's pointer routing. */
    _hitArea = null;

    /** Background sky fill (procedural fallback if no skin). */
    _bgGfx = null;

    /** Configurable background state (set in init from stage.background).
     *  `_bgNode` is the image sprite (null for the flat-colour fallback,
     *  which never parallaxes). `_bgFactor` / `_bgBaseX` / `_bgBaseY`
     *  drive applyParallax. */
    _bgNode = null;
    _bgConfig = null;
    _bgFactor = 1;
    _bgBaseX = 0;
    _bgBaseY = 0;

    /** Cached width / height in pixels — convenience for cameras + bounds. */
    pixelWidth = 0;
    pixelHeight = 0;

    /**
     * @param {object} themeManager  SkinResolver instance
     * @param {{ editorMode?: boolean }} [opts]
     */
    constructor(themeManager = null, opts = {}) {
        this._theme = themeManager;
        this._editorMode = !!opts.editorMode;
        this.container.sortableChildren = true;
        this._fx = new PlatformerFxPreview(this);
    }

    /**
     * @param {object} stage parsed stage JSON
     * @param {string} [gameplayLayerId='terrain']
     */
    init(stage, gameplayLayerId = PLATFORMER_LAYERS.terrain) {
        this._gameplayLayerId = gameplayLayerId;
        this.cols     = stage.grid?.cols     ?? PLATFORMER_GRID.cols;
        this.rows     = stage.grid?.rows     ?? PLATFORMER_GRID.rows;
        this.cellSize = stage.grid?.cellSize ?? PLATFORMER_GRID.cellSize;
        this.pixelWidth  = this.cols * this.cellSize;
        this.pixelHeight = this.rows * this.cellSize;
        this._layers = Array.isArray(stage.layers) ? stage.layers : [];
        this._bgConfig = (stage.background && typeof stage.background === 'object') ? stage.background : null;

        this._drawBackground();
        this._drawBackgroundLayers(stage);

        // Build a Container per layer in stage order so painting later
        // layers naturally renders on top of earlier ones (sky →
        // terrain → entities → foreground). Each layer carries its own
        // alpha (opacity) so a designer can fade a whole layer.
        for (const layer of this._layers) {
            const c = new Container();
            c.label = `layer:${layer.id}`;
            c.visible = layer.visible !== false;
            c.alpha = Number.isFinite(layer.opacity) ? layer.opacity : 1;
            this._layerContainers.set(layer.id, c);
            this.container.addChild(c);
        }

        // Parallax bookkeeping — gathered as we paint so the live
        // camera can scroll decoration layers (and individually-tuned
        // items) at a fraction of the world's speed. See applyParallax.
        this._parallaxLayers = [];
        this._parallaxItems = [];

        // Two-pass paint: tiles first (ALL layers), then entities. The
        // editor relies on entities sitting visually on top of tiles
        // they share a cell with — placing a goomba on a brick row
        // should show the goomba over the brick, not occluded.
        for (const layer of this._layers) {
            // A layer is part of the solid collision world when it's
            // explicitly flagged `collides` (new model) or — for legacy
            // stages without the flag — when it's the chosen gameplay
            // layer. Solid layers stay locked to the camera (factor 1)
            // so collision coords match what's drawn; any non-solid
            // layer may parallax.
            const collides = (layer.collides !== undefined)
                ? !!layer.collides
                : (layer.id === gameplayLayerId);
            // A solid layer flagged `oneWay` becomes a one-way platform
            // surface: it lands a falling body on its top edge but lets
            // the body jump up through it and walk past it sideways.
            const oneWay = collides && layer.oneWay === true;
            // Only decoration layers parallax — gameplay-kind layers
            // (terrain + entities) stay locked so collision coords and
            // the runtime-spawned entities line up with what's drawn.
            const canParallax = !collides && layer.kind !== 'gameplay';
            const layerFactor = (canParallax && Number.isFinite(layer.parallax))
                ? layer.parallax : 1;
            if (canParallax && layerFactor !== 1) {
                const lc = this._layerContainers.get(layer.id);
                if (lc) this._parallaxLayers.push({ container: lc, factor: layerFactor });
            }
            // One neighbour-lookup per layer so every autotiled tile's
            // 4-bit mask probe is O(1) during the paint pass below.
            const autoLookup = buildAutotileLookup(layer, PLATFORMER_TILE_DEFS);
            for (const t of (layer.tiles || [])) {
                this._paintTile(layer, t, autoLookup);
                // Union every solid layer into the collision grid (last
                // writer wins on a shared cell — fine for v1). One-way
                // layers go into a separate grid so they only block a
                // downward landing, never sideways/upward motion.
                if (collides) {
                    const tdef = PLATFORMER_TILE_DEFS[t.type];
                    if (tdef?.slope)  this._slopeGrid.set(`${t.c},${t.r}`, t);
                    else if (oneWay)  this._oneWayGrid.set(`${t.c},${t.r}`, t);
                    else              this._tileGrid.set(`${t.c},${t.r}`, t);
                }
                // Per-item parallax override (non-solid layers only —
                // e.g. one cloud drifting slower than its layer). Capture
                // the node now; the `_tileNodes` key is cell-only and may
                // be overwritten by a same-cell gameplay tile later.
                if (canParallax && Number.isFinite(t.parallax)) {
                    const node = this._tileNodes.get(`${t.c},${t.r}`);
                    if (node) {
                        this._parallaxItems.push({
                            node, baseX: node.x,
                            itemFactor: t.parallax, layerFactor,
                        });
                    }
                }
            }
        }
        for (const layer of this._layers) {
            for (const e of (layer.entities || [])) {
                this._paintEntityPlaceholder(layer, e);
            }
        }

        if (this._editorMode) this._drawGridLines();
    }

    /**
     * Offset decoration layers (and per-item overrides) for the current
     * camera X so they scroll at their parallax factor:
     *
     *   factor &lt; 1  → moves slower than the world (far background)
     *   factor = 1  → locks to the world (gameplay / default)
     *   factor &gt; 1  → moves faster than the world (near foreground)
     *
     * Called every frame by PlatformerScene after it positions the
     * world container. A no-op in the editor (never called there), so
     * the editor always shows items at their true authoring positions.
     *
     * Maths (net screen X of a layer = -camX + container.x):
     *   container.x = camX · (1 − factor)              → net = -camX·factor
     *   itemNode.x  = baseX + camX · (layerF − itemF)  → net = baseX - camX·itemF
     */
    applyParallax(camX, camY = 0) {
        // Background image scrolls at its own factor (set per stage).
        // A full-stage-width backdrop stays gap-free for factors in
        // [0, 1] (see _drawBackground). The flat-colour fallback has no
        // _bgNode, so it stays pinned to the world.
        if (this._bgNode) {
            this._bgNode.x = this._bgBaseX + camX * (1 - this._bgFactor);
        }
        // Stacked parallax layers (Phase 4): each shifts opposite the
        // camera by (1 - factor). factor 0 = pinned to screen (deepest),
        // 1 = locked to the world.
        for (const n of this._bgLayerNodes) {
            n.ts.x = camX * (1 - n.parallaxX);
            if (n.parallaxY) n.ts.y = camY * (1 - n.parallaxY);
        }
        for (const L of this._parallaxLayers) {
            L.container.x = camX * (1 - L.factor);
        }
        for (const it of this._parallaxItems) {
            it.node.x = it.baseX + camX * (it.layerFactor - it.itemFactor);
        }
    }

    // ── Cell readers (used by physics + entity logic) ─────────────

    /** @returns {object|null} the raw tile object on the gameplay layer. */
    tileAt(c, r) {
        return this._tileGrid.get(`${c},${r}`) || null;
    }

    /** Inclusive-range solid lookup. Out-of-bounds is solid except
     *  ABOVE the stage (so jumps don't smack the ceiling pre-emptively).
     */
    isSolidAt(c, r) {
        if (c < 0 || c >= this.cols) return true;
        if (r >= this.rows) return true;          // floor of the world
        if (r < 0) return false;                  // open sky above
        const t = this._tileGrid.get(`${c},${r}`);
        if (!t) return false;
        const def = PLATFORMER_TILE_DEFS[t.type];
        return !!def?.solid;
    }

    /**
     * True when the cell holds a ONE-WAY platform tile (a tile on a
     * layer flagged `collides` + `oneWay`). The physics resolver treats
     * these as solid only for a body landing on the top edge.
     */
    isOneWayAt(c, r) {
        return this._oneWayGrid.has(`${c},${r}`);
    }

    /**
     * World-space Y of the slope surface at horizontal pixel `xWorld`
     * inside cell (c, r), or null when the cell holds no slope.
     *
     * The surface is a straight line across the cell. `slope.kind`
     * picks the height curve (45 = full cell, 22lo / 22hi = the two
     * halves of a gentle two-cell ramp) and an odd tile `rot` mirrors
     * it so the same palette entry covers both facings (R to flip).
     */
    slopeFloorY(c, r, xWorld) {
        const t = this._slopeGrid.get(`${c},${r}`);
        if (!t) return null;
        const def = PLATFORMER_TILE_DEFS[t.type];
        const kind = def?.slope?.kind;
        if (!kind) return null;
        const cs = this.cellSize;
        // Normalised x across the cell, 0 = left edge, 1 = right edge.
        let tx = (xWorld - c * cs) / cs;
        tx = Math.max(0, Math.min(1, tx));
        if (((t.rot || 0) % 2) === 1) tx = 1 - tx;   // mirrored facing
        const hNorm =
            kind === '45'   ? tx :
            kind === '22lo' ? tx * 0.5 :
            kind === '22hi' ? 0.5 + tx * 0.5 : 0;
        return (r + 1) * cs - hNorm * cs;
    }

    /** True when the tile at this cell can be head-bonked (brick / ?-block). */
    bumpableAt(c, r) {
        const t = this._tileGrid.get(`${c},${r}`);
        if (!t) return false;
        const def = PLATFORMER_TILE_DEFS[t.type];
        return !!def?.bumpable;
    }

    /**
     * Per-material friction override for the tile at (c, r), or null
     * when the material doesn't specify one (caller then uses the
     * body's base friction). Lets ice slide, sand grip, etc. — see
     * `friction` in PLATFORMER_TILE_DEFS.
     */
    frictionAt(c, r) {
        const t = this._tileGrid.get(`${c},${r}`);
        if (!t) return null;
        const def = PLATFORMER_TILE_DEFS[t.type];
        return def && Number.isFinite(def.friction) ? def.friction : null;
    }

    /** Remove a tile at (c,r) — used when a brick is bonked into pieces. */
    removeTile(c, r) {
        const key = `${c},${r}`;
        if (!this._tileGrid.has(key)) return false;
        this._tileGrid.delete(key);
        const node = this._tileNodes.get(key);
        if (node) {
            if (node.parent) node.parent.removeChild(node);
            node.destroy?.();
            this._tileNodes.delete(key);
        }
        return true;
    }

    /** Replace a tile at (c,r) with a new tile type (e.g. spent ?-block
     *  → block.hard). Re-paints the cell with the new sprite. */
    replaceTile(c, r, newType) {
        const def = PLATFORMER_TILE_DEFS[newType];
        if (!def) return;
        const key = `${c},${r}`;
        const old = this._tileGrid.get(key);
        const layer = this._layers.find((l) => l.id === this._gameplayLayerId);
        if (!layer || !old) return;
        old.type = newType;
        // Drop the old visual; paint the new one in its place.
        const node = this._tileNodes.get(key);
        if (node && node.parent) node.parent.removeChild(node);
        this._tileNodes.delete(key);
        this._paintTile(layer, old);
    }

    /** Walk every entity across every layer. The runtime spawns its
     *  Player / Goomba / Koopa instances from this iteration. */
    iterateEntities(cb) {
        for (const layer of this._layers) {
            for (const e of (layer.entities || [])) {
                cb(e, layer);
            }
        }
    }

    // ── Editor support ────────────────────────────────────────────

    /**
     * Click hit-area is the full board rect. The editor adds its own
     * pointer listeners after `init()` finishes.
     */
    getHitArea() {
        if (this._hitArea) return this._hitArea;
        const hit = new Graphics();
        hit.rect(0, 0, this.pixelWidth, this.pixelHeight);
        hit.fill({ color: 0x000000, alpha: 0.001 });
        hit.eventMode = 'static';
        hit.cursor = 'pointer';
        hit.zIndex = 9999;
        this.container.addChild(hit);
        this._hitArea = hit;
        return hit;
    }

    /** Editor only — toggle layer visibility on the existing render. */
    setLayerVisible(layerId, visible) {
        const c = this._layerContainers.get(layerId);
        if (c) c.visible = !!visible;
    }

    /** Editor only — set a layer's opacity (0..1) on the existing
     *  render without a full board rebuild, for smooth slider drags. */
    setLayerAlpha(layerId, alpha) {
        const c = this._layerContainers.get(layerId);
        if (c) c.alpha = Math.max(0, Math.min(1, Number(alpha)));
    }

    /** No-op stub matching TrainSimBoard's API; the platformer
     *  doesn't have a Focus mode yet but the editor calls it. */
    setFocusMode(_v) {}
    isFocusMode() { return false; }

    destroy() {
        // Tear down preview emitters first so their nodes are detached
        // before the container tree is destroyed.
        if (this._fx) this._fx.clear();
        // Detach background-layer sprites (their shared textures stay
        // cached for reuse — we only drop the TilingSprite wrappers).
        for (const n of this._bgLayerNodes) {
            if (n.ts.parent) n.ts.parent.removeChild(n.ts);
            n.ts.destroy?.();
        }
        this._bgLayerNodes = [];
        if (this.container.parent) this.container.parent.removeChild(this.container);
        this.container.removeChildren();
        this.container.destroy({ children: true });
        this._tileGrid.clear();
        this._oneWayGrid.clear();
        this._slopeGrid.clear();
        this._tileNodes.clear();
        this._layerContainers.clear();
        this._layers = [];
        this._hitArea = null;
        this._bgNode = null;
        this._bgGfx = null;
        this._bgConfig = null;
    }

    // ── Painting helpers ──────────────────────────────────────────

    _drawBackground() {
        // Per-stage background settings (parallax / position / scale /
        // fit / opacity). Missing config → sensible defaults that match
        // the old behaviour (cover, locked to world, fully opaque).
        const cfg = this._bgConfig || {};
        const parallax = Number.isFinite(cfg.parallax) ? cfg.parallax : 1;
        const offsetX  = Number.isFinite(cfg.offsetX)  ? cfg.offsetX  : 0;
        const offsetY  = Number.isFinite(cfg.offsetY)  ? cfg.offsetY  : 0;
        const scale    = (Number.isFinite(cfg.scale) && cfg.scale > 0) ? cfg.scale : 1;
        const fit      = cfg.fit || 'cover';
        const opacity  = Number.isFinite(cfg.opacity) ? cfg.opacity : 1;

        this._bgNode = null;
        this._bgFactor = parallax;
        this._bgBaseX = offsetX;
        this._bgBaseY = offsetY;

        // Try the SkinResolver first; otherwise paint a flat sky-blue.
        // The image is sized to the full board rect (× scale) so a
        // parallax factor in [0,1] never reveals an edge as the camera
        // pans. `scale` is baked into the requested size (rather than
        // set on the sprite) because SkinResolver re-clamps sprite.scale
        // when the texture finishes loading.
        if (this._theme) {
            const sprite = this._theme.createSprite(
                'platformer.background',
                this.pixelWidth * scale, this.pixelHeight * scale,
                { fitMode: fit },
            );
            if (sprite) {
                sprite.x = offsetX;
                sprite.y = offsetY;
                sprite.alpha = opacity;
                sprite.zIndex = -10;
                this.container.addChild(sprite);
                this._bgNode = sprite;
                return;
            }
        }
        const g = new Graphics();
        g.rect(0, 0, this.pixelWidth, this.pixelHeight);
        g.fill(PLATFORMER_PALETTE.sky);
        g.alpha = opacity;
        g.zIndex = -10;
        this.container.addChild(g);
        this._bgGfx = g;
    }

    /**
     * Phase 4 — stacked parallax background image layers. Each entry in
     * `stage.backgroundLayers` becomes a full-stage-width TilingSprite
     * sitting between the base backdrop (z -10) and the gameplay tiles
     * (z 0). The texture tiles horizontally so auto-scroll + parallax
     * never reveal an edge for factors in [0, 1].
     *
     * Per layer cfg: { slot, parallaxX, parallaxY, scrollX, scrollY,
     *                  opacity, scale, offsetY, visible }
     *
     * Backward compatible: stages without `backgroundLayers` get nothing
     * here and keep the single `background` backdrop drawn above.
     */
    _drawBackgroundLayers(stage) {
        this._bgLayerNodes = [];
        const layers = Array.isArray(stage.backgroundLayers) ? stage.backgroundLayers : [];
        layers.forEach((cfg, i) => {
            if (!cfg || cfg.visible === false) return;
            const src = this._theme?.get?.(cfg.slot);
            if (!src) return;   // no art assigned yet → draw nothing

            const ts = new TilingSprite({
                texture: Texture.EMPTY,
                width: this.pixelWidth,
                height: this.pixelHeight,
            });
            ts.zIndex = -9 + i * 0.1;   // far→near, all behind tiles (z 0)
            ts.alpha = Number.isFinite(cfg.opacity) ? cfg.opacity : 1;
            ts.x = 0;
            ts.y = 0;
            this.container.addChild(ts);

            const node = {
                ts,
                parallaxX: Number.isFinite(cfg.parallaxX) ? cfg.parallaxX : 0.5,
                parallaxY: Number.isFinite(cfg.parallaxY) ? cfg.parallaxY : 0,
                scrollX: Number.isFinite(cfg.scrollX) ? cfg.scrollX : 0,
                scrollY: Number.isFinite(cfg.scrollY) ? cfg.scrollY : 0,
                scrollOffX: 0,
                scrollOffY: 0,
                scale: (Number.isFinite(cfg.scale) && cfg.scale > 0) ? cfg.scale : 1,
                offsetY: Number.isFinite(cfg.offsetY) ? cfg.offsetY : 0,
            };
            this._bgLayerNodes.push(node);

            this._loadBgTexture(src, (tex) => {
                if (!node.ts || node.ts.destroyed) return;
                ts.texture = tex;
                // Fill the stage height by default; `scale` zooms on top.
                const baseScale = tex.height ? (this.pixelHeight / tex.height) : 1;
                ts.tileScale.set(baseScale * node.scale);
                ts.tilePosition.y = node.offsetY;
            });
        });
    }

    /** Load (and cache) a background image into a Texture, then call cb. */
    _loadBgTexture(src, cb) {
        const cache = PlatformerBoard._bgTexCache;
        const cached = cache.get(src);
        if (cached) { cb(cached); return; }
        const img = new Image();
        img.onload = () => {
            const tex = Texture.from(img);
            if (tex?.source) {
                tex.source.scaleMode = 'linear';
                tex.source.autoGenerateMipmaps = true;
            }
            cache.set(src, tex);
            cb(tex);
        };
        img.onerror = () => { /* missing art — leave layer empty */ };
        img.src = src;
    }

    /**
     * Advance auto-scroll on every background layer (px/sec). Cheap
     * no-op when no layer scrolls. Called from the runtime scene + the
     * editor preview loop. `dt` is in seconds.
     */
    tickBackgrounds(dt) {
        if (!this._bgLayerNodes.length || !(dt > 0)) return;
        for (const n of this._bgLayerNodes) {
            if (n.scrollX) {
                n.scrollOffX -= n.scrollX * dt;
                n.ts.tilePosition.x = n.scrollOffX;
            }
            if (n.scrollY) {
                n.scrollOffY -= n.scrollY * dt;
                n.ts.tilePosition.y = n.offsetY + n.scrollOffY;
            }
        }
    }

    _drawGridLines() {
        const g = new Graphics();
        g.zIndex = 90;
        const step = this.cellSize;
        for (let c = 0; c <= this.cols; c++) {
            g.moveTo(c * step, 0);
            g.lineTo(c * step, this.pixelHeight);
        }
        for (let r = 0; r <= this.rows; r++) {
            g.moveTo(0, r * step);
            g.lineTo(this.pixelWidth, r * step);
        }
        g.stroke({ color: 0xffffff, width: 1, alpha: 0.12 });
        this.container.addChild(g);
    }

    _paintTile(layer, t, autoLookup = null) {
        const def = PLATFORMER_TILE_DEFS[t.type];
        if (!def) return;
        const layerC = this._layerContainers.get(layer.id);
        if (!layerC) return;

        const fw = def.footprint?.w || 1;
        const fh = def.footprint?.h || 1;
        const w = fw * this.cellSize;
        const h = fh * this.cellSize;
        const x = t.c * this.cellSize;
        const y = t.r * this.cellSize;

        // Autotiling: derive the 4-bit neighbour mask and render the
        // matching cell of the group's 4×4 template image. When no
        // template art is imported `createSprite` returns null and we
        // fall through to the tile's normal single-image art.
        let sprite = null;
        let mask = null;
        if (def.autotile) {
            const lookup = autoLookup || buildAutotileLookup(layer, PLATFORMER_TILE_DEFS);
            const group = def.autotile.group;
            mask = autotileMask((cc, rr) => lookup.get(`${cc},${rr}`) === group, t.c, t.r);
            sprite = this._theme?.createSprite?.(def.autotile.slot, w, h, {
                fitMode: 'stretch', anchorX: 0, anchorY: 0,
                region: autotileRegion(mask),
            });
        }
        // Slopes mirror their art when the tile faces left (odd rot) so
        // one imported PNG (drawn rising to the RIGHT) covers both.
        // flipX negates the sprite's X scale, which mirrors around the
        // anchor — anchorX: 1 keeps the flipped image inside the cell.
        const slopeMirrored = def.slope ? ((t.rot || 0) % 2) === 1 : false;
        if (!sprite) {
            sprite = this._theme?.createSprite?.(def.slot, w, h, {
                fitMode: 'cover', anchorX: slopeMirrored ? 1 : 0, anchorY: 0,
                flipX: slopeMirrored,
            });
        }
        let node;
        if (sprite) {
            sprite.x = x;
            sprite.y = y;
            node = sprite;
        } else {
            node = drawTilePlaceholder(t.type, def, w, h, mask, t.rot || 0);
            node.x = x;
            node.y = y;
        }
        layerC.addChild(node);
        this._tileNodes.set(`${t.c},${t.r}`, node);
    }

    _paintEntityPlaceholder(layer, e) {
        const def = PLATFORMER_ENTITY_DEFS[e.type];
        if (!def) return;
        const layerC = this._layerContainers.get(layer.id);
        if (!layerC) return;

        // Area / trigger zones span w×h cells and have no artwork — draw
        // a tinted rectangle so the designer can see + click the region.
        // In game mode the runtime's AreaTriggerEntity handles its own
        // (optional) rendering, so the board skips it there.
        if (e.type === 'area.trigger') {
            if (!this._editorMode) return;
            paintAreaPlaceholder(layerC, e, this.cellSize);
            return;
        }

        // Camera zones span w×h cells and have no in-game artwork — the
        // runtime CameraZoneEntity is a pure logic region. Draw a labelled
        // rectangle in the editor so it can be seen + clicked + resized.
        if (e.type === 'camera.zone') {
            if (!this._editorMode) return;
            paintCameraZonePlaceholder(layerC, e, this.cellSize);
            return;
        }

        // Point light — no in-game artwork (the runtime lightmap draws the
        // glow). The editor paints a bulb + reach ring so designers can aim
        // and size it against the level.
        if (e.type === 'light.point') {
            if (!this._editorMode) return;
            paintLightPlaceholder(layerC, e, this.cellSize);
            return;
        }

        // Moving platform — the runtime spawns its own MovingPlatformEntity
        // (the live slab). The editor draws the slab + its travel path +
        // a ghost at the far endpoint so the sweep is readable at a glance.
        if (e.type === 'platform.moving') {
            if (!this._editorMode) return;
            paintPlatformPlaceholder(layerC, e, this.cellSize);
            return;
        }

        // Particle emitter has no artwork. The runtime spawns a live
        // ParticleEmitterEntity; in the editor we draw a small marker
        // AND run a live preview ParticleSystem so the designer sees the
        // effect immediately while tuning it.
        if (e.type === 'fx.emitter') {
            if (!this._editorMode) return;
            this._fx.addPreview(layerC, e);
            return;
        }

        const w = this.cellSize;
        const h = this.cellSize;
        const x = e.c * this.cellSize;
        const y = e.r * this.cellSize;

        // In editor mode we ALWAYS draw a placeholder over any sprite
        // so the designer can see and click the entity. In game mode
        // we skip painting here entirely — the runtime spawns its own
        // entity instances (CoinEntity, FlagGoal, Player, …) which own
        // both the visual and the pickup logic. (The board used to
        // also paint coins in-game; that drew a second, un-pickable
        // ghost coin over every live one once real art shipped.)
        if (!this._editorMode) return;

        // Editor: prefer the real art (uploaded sprite / mapped tileset
        // region). When no PNG is set, draw a stylised default shape for
        // the entity type so it reads as a goomba / player / coin rather
        // than an anonymous coloured circle.
        const sprite = this._theme?.createSprite?.(def.slot, w, h, {
            fitMode: 'contain', anchorX: 0.5, anchorY: 0.5,
        });
        let node;
        if (sprite) {
            sprite.x = x + w / 2;
            sprite.y = y + h / 2;
            node = sprite;
        } else {
            node = drawEntityPlaceholder(e.type, def, w, h);
            node.x = x;
            node.y = y;
        }
        layerC.addChild(node);

        // Subtle outline around the cell so it's clear which cell the
        // entity occupies even behind a sprite (and stays clickable).
        const ring = new Graphics();
        ring.rect(1, 1, w - 2, h - 2);
        ring.stroke({ color: def.fallbackColor, width: 1.5, alpha: 0.5 });
        ring.x = x;
        ring.y = y;
        layerC.addChild(ring);
    }

    /**
     * Advance every editor preview emitter. Called from the editor's
     * per-frame update loop (cheap no-op when there are no emitters).
     * `delta` is in 60 FPS frames; particles think in seconds. Delegated to
     * PlatformerFxPreview, which also sweeps the selected emitter's demo ghost.
     */
    tickParticles(delta) { this._fx.tick(delta); }

    /**
     * Editor selection → which emitter shows its force gizmos + demo ghost.
     * `id` is an entity id, or null to clear. Idempotent.
     */
    setSelectedEmitter(id) { this._fx.setSelected(id); }

    /**
     * Live-tune a running emitter preview in place (no rebuild, no emission
     * restart). Returns false when the edit flips the PNG/image render mode,
     * which the caller resolves with a full board rebuild.
     */
    updateEmitterLive(entity) { return this._fx.updateLive(entity); }
}
